<template>
  <div class="container-fluid py-4">

    <div class="card my-4 shadow-sm">
      <div class="card-header text-white" style="background:#b03a45;">
        <h6 class="mb-0 text-white">
          {{ t("salaryAdvance.title") }}
        </h6>
      </div>

      <div class="card-body">

        <!-- FORM -->
        <form @submit.prevent="submitSalaryAdvance">
          <div class="row">

            <!-- Amount -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">
                {{ t("salaryAdvance.amount") }}
              </label>
              <input
                type="number"
                class="form-control custom-border"
                v-model.number="form.amount"
                required
                :placeholder="t('salaryAdvance.amountPlaceholder')"
              />
            </div>

            <!-- Type -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">
                {{ t("salaryAdvance.type") }}
              </label>
              <select v-model="form.type_advance" class="form-select custom-border" required>
                <option disabled value="">
                  {{ t("salaryAdvance.selectType") }}
                </option>
                <option value="cash">
                  {{ t("salaryAdvance.cash") }}
                </option>
                <option value="gift_voucher">
                  {{ t("salaryAdvance.giftVoucher") }}
                </option>
              </select>
            </div>

            <!-- Reason -->
            <div class="col-12 mb-3">
              <label class="form-label fw-semibold">
                {{ t("salaryAdvance.reason") }}
              </label>
              <div class="border rounded p-2">
                <textarea
                  v-model="form.reason"
                  class="form-control border-0"
                  rows="4"
                  :placeholder="t('salaryAdvance.reasonPlaceholder')"
                  style="resize: vertical;"
                ></textarea>
              </div>
            </div>

            <!-- Submit -->
            <div class="col-12 text-end">
              <button
                class="btn text-white"
                style="background:#b03a45;"
                :disabled="submitting"
              >
                {{
                  submitting
                    ? t("salaryAdvance.submitting")
                    : t("salaryAdvance.submit")
                }}
              </button>
            </div>

          </div>
        </form>

        <!-- LIST -->
        <div class="mt-5">
          <h6 class="fw-bold">
            {{ t("salaryAdvance.myRequests") }}
          </h6>

          <div v-if="loading">
            {{ t("salaryAdvance.loading") }}
          </div>

          <div v-else-if="advances.length === 0" class="text-muted">
            {{ t("salaryAdvance.empty") }}
          </div>

          <div v-else class="table-responsive">
            <table class="table table-striped align-middle mt-2">
              <thead>
                <tr>
                  <th>{{ t("salaryAdvance.table.type") }}</th>
                  <th>{{ t("salaryAdvance.table.amount") }}</th>
                  <th>{{ t("salaryAdvance.table.status") }}</th>
                  <th>{{ t("salaryAdvance.table.supervisor") }}</th>
                  <th>{{ t("salaryAdvance.table.reason") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="adv in advances" :key="adv.id">
                  <td>{{ adv.type_advance }}</td>
                  <td>{{ adv.amount }} TND</td>
                  <td>
                    <span :class="{
                      'text-warning': adv.status === 'Pending',
                      'text-success': adv.status === 'Approved',
                      'text-danger': adv.status === 'Rejected'
                    }">
                      {{
                        adv.status === "Pending"
                          ? t("salaryAdvance.status.pending")
                          : adv.status === "Approved"
                          ? t("salaryAdvance.status.approved")
                          : t("salaryAdvance.status.rejected")
                      }}
                    </span>
                  </td>
                  <td>
                    {{ adv.supervisor?.firstname }}
                    {{ adv.supervisor?.lastname || "—" }}
                  </td>
                  <td>{{ adv.reason || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import axios from "@/api";
import { useToast } from "vue-toastification";

const { t } = useI18n();
const toast = useToast();

const form = ref({
  amount: "",
  type_advance: "",
  reason: ""
});

const advances = ref([]);
const loading = ref(false);
const submitting = ref(false);

const fetchMyAdvances = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get("/employee/salary-advances/user/me");
    advances.value = data.advances || [];
  } catch (err) {
    toast.error(t("salaryAdvance.toast.loadError"));
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const submitSalaryAdvance = async () => {
  if (!form.value.amount || !form.value.type_advance) {
    toast.error(t("salaryAdvance.toast.required"));
    return;
  }

  submitting.value = true;
  try {
    await axios.post("/employee/salary-advances", {
      amount: form.value.amount,
      type_advance: form.value.type_advance,
      reason: form.value.reason || ""
    });

    toast.success(t("salaryAdvance.toast.success"));
    form.value = { amount: "", type_advance: "", reason: "" };
    await fetchMyAdvances();

  } catch (err) {
    if (err.response?.status === 422) {
      toast.error(t("salaryAdvance.toast.validation"));
    } else if (err.response?.status === 403) {
      toast.error(t("salaryAdvance.toast.unauthorized"));
    } else {
      toast.error(t("salaryAdvance.toast.failed"));
    }
    console.error(err);
  } finally {
    submitting.value = false;
  }
};

onMounted(fetchMyAdvances);
</script>

<style scoped>
/* Custom border for Amount and Type fields */
.custom-border {
  border: 2px solid #ccc; /* Light gray border */
}

.custom-border:focus {
  border: 2px solid #ccc; /* Keep the border color on focus */
  box-shadow: none; /* Remove any shadow on focus */
}

/* Styling the submit button */
.btn {
  background-color: #b03a45;
}

.table th {
  font-size: .875rem;
  text-transform: uppercase;
  letter-spacing: .025em;
}

.table td {
  vertical-align: middle;
}

.badge {
  padding: .35em .65em;
  border-radius: .5rem;
  text-transform: uppercase;
  font-weight: 600;
}

.text-warning {
  color: #ffc107;
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: #dc3545;
}
</style>
